import React, { useContext, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

import { API_URL } from "../../components/api";
import LanguageContext from "../../context/LanguageContext";

import OnlineReceiptLinksModal from "./OnlineReceiptLinksModal";
import "./OnlineDrawerBreakdown.css";


function OnlineDrawerBreakdown({ method, balance }) {
  const { direction } = useContext(LanguageContext);

  const [show, setShow] = useState(false);
  const [rows, setRows] = useState([]);
  const [methodAccounts, setMethodAccounts] = useState([]);
  const [
    selectedOnlineAccountId,
    setSelectedOnlineAccountId,
  ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [
    linkedReceiptDetail,
    setLinkedReceiptDetail,
  ] = useState([]);


  const [currentBalance, setCurrentBalance] = useState({
    balance_usd: 0,
    balance_lbp: 0,
  });

  const [explainedBalance, setExplainedBalance] = useState({
    balance_usd: "0.00",
    balance_lbp: "0.00",
  });

  const isArabic = direction === "rtl";
  const methodLabel = method === "whish" ? "Whish" : "OMT";

  const text = isArabic
    ? {
        current: "الرصيد الحالي",
        total: "الإجمالي",
        transaction: "المعاملة",
        date: "التاريخ",
        type: "النوع",
        client: "العميل",
        addedBy: "أضيف بواسطة",
        receipt: "الإيصال",
        invoice: "الفاتورة",
        amountUsd: "المبلغ USD",
        feeUsd: "الرسوم USD",
        changeUsd: "التغيير USD",
        balanceUsd: "الرصيد USD",
        amountLbp: "المبلغ LBP",
        feeLbp: "الرسوم LBP",
        changeLbp: "التغيير LBP",
        balanceLbp: "الرصيد LBP",
        destination: "الصندوق الوجهة",
        description: "السبب / الوصف",
        collection: "تحصيل",
        cashOut: "سحب نقدي",
        partialCashOut: "سحب نقدي جزئي",
        reversal: "عكس",
        loading: "جارٍ تحميل تفاصيل الرصيد...",
        empty: "لا توجد معاملات قائمة لهذا الرصيد.",
        failed: "تعذّر تحميل تفاصيل الرصيد.",
        mismatch:
          "المعاملات لا تفسر الرصيد الحالي بشكل مطابق.",
      }
    : {
        current: "Current Balance",
        total: "Total",
        transaction: "Transaction",
        date: "Date",
        type: "Type",
        client: "Client",
        addedBy: "Added By",
        receipt: "Receipt",
        invoice: "Invoice",
        amountUsd: "Amount USD",
        feeUsd: "Fee USD",
        changeUsd: "Net Change USD",
        balanceUsd: "Balance USD",
        amountLbp: "Amount LBP",
        feeLbp: "Fee LBP",
        changeLbp: "Net Change LBP",
        balanceLbp: "Balance LBP",
        destination: "Destination",
        description: "Reason / Description",
        collection: "Collection",
        cashOut: "Cash Out",
        partialCashOut: "Partial Cash Out",
        reversal: "Reversal",
        loading: "Loading current balance details...",
        empty:
          "There are no outstanding transactions for this balance.",
        failed: "Unable to load current balance details.",
        mismatch:
          "Transactions do not exactly explain the current balance.",
      };


  const toCents = (value) => {
    const raw = String(value ?? "0").trim();

    const match = raw.match(
      /^(-)?(\d+)(?:\.(\d+))?$/
    );

    if (!match) {
      throw new Error("INVALID_MONEY");
    }

    const sign = match[1] ? -1 : 1;
    const whole = Number(match[2]);
    const decimal = (
      (match[3] || "") + "00"
    ).slice(0, 2);

    return sign * (
      whole * 100 + Number(decimal)
    );
  };


  const fromCents = (value) => {
    const sign = value < 0 ? "-" : "";
    const absolute = Math.abs(value);

    return (
      sign
      + Math.floor(absolute / 100)
      + "."
      + String(
        absolute % 100
      ).padStart(2, "0")
    );
  };


  const formatMoney = (
    value,
    signed = false
  ) => {
    const cents = toCents(value);
    const formatted = fromCents(cents);

    if (signed && cents > 0) {
      return `+${formatted}`;
    }

    return formatted;
  };


  const hasBalance =
    toCents(balance?.balance_usd ?? 0) !== 0
    ||
    toCents(balance?.balance_lbp ?? 0) !== 0;


  const loadAllRows = async (onlineAccountId) => {
    const authTokens = JSON.parse(
      localStorage.getItem("authTokens")
    );

    const getPage = (page) =>
      axios.get(
        `${API_URL}/accounting/online-drawer/`,
        {
          headers: {
            Authorization:
              `Bearer ${authTokens.access}`,
          },
          params: {
            online_account_id: onlineAccountId,
            page,
            page_size: 100,
          },
        }
      );

    const first = await getPage(1);

    if (
      String(first.data.selected_online_account_id)
      !== String(onlineAccountId)
    ) {
      throw new Error("ONLINE_ACCOUNT_MISMATCH");
    }

    const history = [
      ...(first.data.results || []),
    ];

    const totalPages = Number(
      first.data.pagination?.total_pages || 1
    );

    for (
      let page = 2;
      page <= totalPages;
      page += 1
    ) {
      const response = await getPage(page);

      history.push(
        ...(response.data.results || [])
      );
    }

    return {
      history,
      current: {
        balance_usd:
          first.data.balance_usd ?? 0,
        balance_lbp:
          first.data.balance_lbp ?? 0,
      },
    };
  };


  const explain = (
    history,
    current
  ) => {
    const wantedUsd = toCents(
      current.balance_usd
    );

    const wantedLbp = toCents(
      current.balance_lbp
    );


    if (
      wantedUsd === 0
      && wantedLbp === 0
    ) {
      return {
        rows: [],
        usd: 0,
        lbp: 0,
      };
    }


    const chronological = [
      ...history,
    ].sort((a, b) => {
      const dateDifference =
        new Date(a.creation_date).getTime()
        -
        new Date(b.creation_date).getTime();

      if (dateDifference !== 0) {
        return dateDifference;
      }

      return (
        Number(
          a.online_drawer_transaction_id
        )
        -
        Number(
          b.online_drawer_transaction_id
        )
      );
    });


    let runningUsd = 0;
    let runningLbp = 0;
    let lastZeroIndex = -1;


    chronological.forEach(
      (row, index) => {
        runningUsd += toCents(
          row.balance_change_usd
        );

        runningLbp += toCents(
          row.balance_change_lbp
        );

        if (
          runningUsd === 0
          && runningLbp === 0
        ) {
          lastZeroIndex = index;
        }
      }
    );


    const outstanding = chronological.slice(
      lastZeroIndex + 1
    );

    let explainedUsd = 0;
    let explainedLbp = 0;


    const explainedRows = outstanding.map(
      (row) => {
        explainedUsd += toCents(
          row.balance_change_usd
        );

        explainedLbp += toCents(
          row.balance_change_lbp
        );


        const partialCashOut =
          row.transaction_type === "cash_out"
          &&
          (
            explainedUsd !== 0
            || explainedLbp !== 0
          );


        return {
          ...row,

          method_balance_after_usd:
            fromCents(explainedUsd),

          method_balance_after_lbp:
            fromCents(explainedLbp),

          is_partial_cash_out:
            partialCashOut,
        };
      }
    );


    if (
      explainedUsd !== wantedUsd
      ||
      explainedLbp !== wantedLbp
    ) {
      throw new Error(
        "RECONCILIATION_FAILED"
      );
    }


    return {
      rows: explainedRows,
      usd: explainedUsd,
      lbp: explainedLbp,
    };
  };


  const applyAccountBreakdown = async (
    onlineAccountId
  ) => {
    const result = await loadAllRows(
      onlineAccountId
    );

    setCurrentBalance(
      result.current
    );

    const explanation = explain(
      result.history,
      result.current
    );

    setRows(
      explanation.rows
    );

    setExplainedBalance({
      balance_usd:
        fromCents(explanation.usd),
      balance_lbp:
        fromCents(explanation.lbp),
    });
  };


  const loadMethodAccounts = async () => {
    const authTokens = JSON.parse(
      localStorage.getItem("authTokens")
    );

    const response = await axios.get(
      `${API_URL}/accounting/online-drawer/`,
      {
        headers: {
          Authorization:
            `Bearer ${authTokens.access}`,
        },
        params: {
          page: 1,
          page_size: 1,
        },
      }
    );

    return (
      response.data.online_accounts || []
    ).filter(
      (account) =>
        account.payment_method === method
    );
  };


  const openBreakdown = async () => {
    setShow(true);
    setLoading(true);
    setError("");
    setRows([]);

    try {
      const accounts =
        await loadMethodAccounts();

      setMethodAccounts(accounts);

      if (accounts.length === 0) {
        setSelectedOnlineAccountId("");

        setCurrentBalance({
          balance_usd: 0,
          balance_lbp: 0,
        });

        setExplainedBalance({
          balance_usd: "0.00",
          balance_lbp: "0.00",
        });

        return;
      }

      const accountId = String(
        accounts[0].online_account_id
      );

      setSelectedOnlineAccountId(
        accountId
      );

      await applyAccountBreakdown(
        accountId
      );
    } catch (err) {
      console.error(err);

      setError(
        err.message === "RECONCILIATION_FAILED"
          ? text.mismatch
          : text.failed
      );
    } finally {
      setLoading(false);
    }
  };


  const selectOnlineAccount = async (
    onlineAccountId
  ) => {
    if (
      String(onlineAccountId)
      === String(selectedOnlineAccountId)
    ) {
      return;
    }

    setLoading(true);
    setError("");
    setRows([]);

    try {
      const accountId = String(
        onlineAccountId
      );

      setSelectedOnlineAccountId(
        accountId
      );

      await applyAccountBreakdown(
        accountId
      );
    } catch (err) {
      console.error(err);

      setError(
        err.message === "RECONCILIATION_FAILED"
          ? text.mismatch
          : text.failed
      );
    } finally {
      setLoading(false);
    }
  };


  const typeLabel = (row) => {
    if (
      row.transaction_type === "cash_out"
      && row.is_partial_cash_out
    ) {
      return text.partialCashOut;
    }

    if (row.transaction_type === "cash_out") {
      return text.cashOut;
    }

    if (row.transaction_type === "collection") {
      return text.collection;
    }

    if (row.transaction_type === "reversal") {
      return text.reversal;
    }

    return row.transaction_type || "-";
  };


  const showUsd =
    toCents(balance?.balance_usd ?? 0) !== 0
    ||
    rows.some(
      (row) =>
        toCents(row.amount_usd) !== 0
        ||
        toCents(row.fee_usd) !== 0
        ||
        toCents(row.balance_change_usd) !== 0
    );

  const showLbp =
    toCents(balance?.balance_lbp ?? 0) !== 0
    ||
    rows.some(
      (row) =>
        toCents(row.amount_lbp) !== 0
        ||
        toCents(row.fee_lbp) !== 0
        ||
        toCents(row.balance_change_lbp) !== 0
    );

  const usdFields = [
    "amount_usd",
    "fee_usd",
    "balance_change_usd",
    "method_balance_after_usd",
  ];

  const lbpFields = [
    "amount_lbp",
    "fee_lbp",
    "balance_change_lbp",
    "method_balance_after_lbp",
  ];


  const columns = [
    {
      field: "online_drawer_transaction_id",
      headerName: text.transaction,
      minWidth: 100,
    },
    {
      field: "creation_date",
      headerName: text.date,
      minWidth: 180,
      renderCell: (params) => {
        const date = new Date(
          params.value
        );

        return Number.isNaN(
          date.getTime()
        )
          ? "-"
          : date.toLocaleString();
      },
    },
    {
      field: "transaction_type",
      headerName: text.type,
      minWidth: 150,
      renderCell: (params) =>
        typeLabel(params.row),
    },
    {
      field: "client_name",
      headerName: text.client,
      minWidth: 210,
    },
      {
        field: "initiator",
        headerName: text.addedBy,
        minWidth: 130,
        renderCell: (params) =>
          params.value || "-",
      },
      {
        field: "receipt_display",
        headerName: text.receipt,
        minWidth: 160,
        renderCell: ({ row }) => {
          const linked = Array.isArray(
            row.linked_receipts
          ) && row.linked_receipts.length > 0;

          const label =
            row.receipt_display || "-";

          if (!linked) {
            return label;
          }

          return (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();

                setLinkedReceiptDetail(
                  row.linked_receipts
                );
              }}
              style={{
                padding: 0,
                border: "none",
                background: "transparent",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: "invoice_display",
        headerName: text.invoice,
        minWidth: 160,
        renderCell: ({ row }) => {
          const linked = Array.isArray(
            row.linked_receipts
          ) && row.linked_receipts.length > 0;

          const label =
            row.invoice_display || "-";

          if (!linked) {
            return label;
          }

          return (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();

                setLinkedReceiptDetail(
                  row.linked_receipts
                );
              }}
              style={{
                padding: 0,
                border: "none",
                background: "transparent",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        },
      },
    {
      field: "amount_usd",
      headerName: text.amountUsd,
      minWidth: 115,
      renderCell: (params) =>
        formatMoney(params.value),
    },
    {
      field: "fee_usd",
      headerName: text.feeUsd,
      minWidth: 100,
      renderCell: (params) =>
        formatMoney(params.value),
    },
    {
      field: "balance_change_usd",
      headerName: text.changeUsd,
      minWidth: 130,
      renderCell: (params) =>
        formatMoney(
          params.value,
          true
        ),
    },
    {
      field: "method_balance_after_usd",
      headerName: text.balanceUsd,
      minWidth: 120,
    },
    {
      field: "amount_lbp",
      headerName: text.amountLbp,
      minWidth: 120,
      renderCell: (params) =>
        formatMoney(params.value),
    },
    {
      field: "fee_lbp",
      headerName: text.feeLbp,
      minWidth: 100,
      renderCell: (params) =>
        formatMoney(params.value),
    },
    {
      field: "balance_change_lbp",
      headerName: text.changeLbp,
      minWidth: 130,
      renderCell: (params) =>
        formatMoney(
          params.value,
          true
        ),
    },
    {
      field: "method_balance_after_lbp",
      headerName: text.balanceLbp,
      minWidth: 120,
    },
    {
      field: "destination_drawer",
      headerName: text.destination,
      minWidth: 150,
    },
    {
      field: "description",
      headerName: text.description,
      minWidth: 300,
    },
  ].filter((column) => {
    if (
      !showUsd
      && usdFields.includes(column.field)
    ) {
      return false;
    }

    if (
      !showLbp
      && lbpFields.includes(column.field)
    ) {
      return false;
    }

    return true;
  });


  return (
    <>
      <button
        type="button"
        className={
          "accounting-online-balance"
          + (
            hasBalance
              ? " clickable"
              : ""
          )
        }
        onClick={openBreakdown}
      >
        {methodLabel}: USD{" "}
        {balance?.balance_usd ?? 0}
        {" · "}LBP{" "}
        {balance?.balance_lbp ?? 0}
      </button>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        className="online-drawer-breakdown-modal"
        dir={direction}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {methodLabel} — {text.current}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="online-drawer-breakdown-summary">
            <div>
              <strong>
                {text.current}:
              </strong>
              {" "}USD{" "}
              {formatMoney(
                currentBalance.balance_usd
              )}
              {" · "}LBP{" "}
              {formatMoney(
                currentBalance.balance_lbp
              )}
            </div>

          </div>

            <div className="online-drawer-holder-tabs">
              {methodAccounts.map((account) => {
                const active =
                  String(account.online_account_id)
                  === String(selectedOnlineAccountId);

                return (
                  <button
                    type="button"
                    key={account.online_account_id}
                    className={
                      active
                        ? "online-drawer-holder-tab online-drawer-holder-tab-active"
                        : "online-drawer-holder-tab"
                    }
                    onClick={() =>
                      selectOnlineAccount(
                        account.online_account_id
                      )
                    }
                    disabled={loading}
                  >
                    <span>
                      {account.account_holder_name}
                    </span>

                    <small>
                      USD{" "}
                      {formatMoney(
                        account.balance_usd
                      )}
                      {" · "}LBP{" "}
                      {formatMoney(
                        account.balance_lbp
                      )}
                    </small>
                  </button>
                );
              })}
            </div>

          {loading ? (
            <div className="online-drawer-breakdown-message">
              {text.loading}
            </div>

          ) : error ? (
            <div className="online-drawer-breakdown-error">
              {error}
            </div>

          ) : rows.length === 0 ? (
            <div className="online-drawer-breakdown-message">
              {text.empty}
            </div>

          ) : (
            <div className="online-drawer-breakdown-scroll">
              <div className="online-drawer-breakdown-grid">
                <DataGrid
                  autoHeight
                  rows={rows}
                  columns={columns}
                  getRowId={(row) =>
                    row.online_drawer_transaction_id
                  }
                  pageSize={25}
                  rowsPerPageOptions={[
                    25,
                    50,
                    100,
                  ]}
                  pagination
                  disableSelectionOnClick
                />

                <div className="online-drawer-breakdown-total">
                  <strong>{text.total}:{" "}</strong>
                  {" "}USD{" "}
                  {formatMoney(
                    explainedBalance.balance_usd
                  )}
                  {" · "}LBP{" "}
                  {formatMoney(
                    explainedBalance.balance_lbp
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      <OnlineReceiptLinksModal
        show={linkedReceiptDetail.length > 0}
        onHide={() => setLinkedReceiptDetail([])}
        rows={linkedReceiptDetail}
        className="online-drawer-breakdown-modal"
        isArabic={isArabic}
      />

    </>
  );
}


export default OnlineDrawerBreakdown;
